/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Switch, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { CategoryFilterState } from '../../domain/catalogCategoryTypes';
import type { CategoryRecord } from '../../domain/categoryTypes';
import type { VisibleCategoryGroup } from '../../domain/categoryMasterTree';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors } from '../../../shared/theme/ticketColors'
import { typography } from '../../../shared/theme/typography';
import { ticketSurfaces } from '../../../shared/ticket/ticketSurfaces.styles';
import { usePublicStoreFeaturesVisible } from '../../../shell/feature-flags/usePublicStoreFeaturesVisible';

export type CategoryMasterTreeViewProps = {
  groups: VisibleCategoryGroup[];
  canEdit: boolean;
  togglingCategoryIds: ReadonlySet<string>;
  filterState: CategoryFilterState;
  isWide: boolean;
  reordering?: boolean;
  /** Modo ordenar activo: deshabilita switches/detalle para que no compitan con el reorder. */
  reorderMode?: boolean;
  onOpenDetail: (category: CategoryRecord) => void;
  onToggleActive: (category: CategoryRecord, nextActive: boolean) => void;
  /** Mueve la categoría un puesto dentro de su nivel (padres entre padres, hijas entre hermanas). */
  onMoveCategory?: (category: CategoryRecord, direction: -1 | 1) => void;
};

function ReorderArrows({
  category,
  canMoveUp,
  canMoveDown,
  disabled,
  onMove,
}: {
  category: CategoryRecord;
  canMoveUp: boolean;
  canMoveDown: boolean;
  disabled: boolean;
  onMove: (category: CategoryRecord, direction: -1 | 1) => void;
}) {
  const btn = (direction: -1 | 1, enabled: boolean, icon: 'chevron-up' | 'chevron-down', label: string) => {
    const active = enabled && !disabled;
    return (
      <Pressable
        style={[styles.moveBtn, !active && styles.moveBtnOff]}
        disabled={!active}
        hitSlop={6}
        onPress={() => onMove(category, direction)}
        accessibilityRole="button"
        accessibilityLabel={label}
        testID={`category-master-move-${direction === -1 ? 'up' : 'down'}-${category.id}`}
      >
        <Ionicons
          name={icon}
          size={18}
          color={active ? ticketColors.ink2 : ticketColors.ruleSoft}
        />
      </Pressable>
    );
  };
  return (
    <View style={styles.moveCol}>
      {btn(-1, canMoveUp, 'chevron-up', `Subir ${category.name}`)}
      {btn(1, canMoveDown, 'chevron-down', `Bajar ${category.name}`)}
    </View>
  );
}

function categoryAppliesLabel(category: CategoryRecord) {
  if (category.appliesTo === 'service') return 'Servicios';
  if (category.appliesTo === 'both') return 'Productos y servicios';
  return 'Productos';
}

function categoryChildrenMeta(childCount: number, category: CategoryRecord) {
  const count = childCount > 0 ? `${childCount} subcategorías` : 'Sin subcategorías';
  return `${count} · ${categoryAppliesLabel(category)}`;
}

function CategoryActiveSwitch({
  category,
  active,
  disabled,
  label,
  onToggleActive,
}: {
  category: CategoryRecord;
  active: boolean;
  disabled: boolean;
  label: string;
  onToggleActive: (category: CategoryRecord, nextActive: boolean) => void;
}) {
  return (
    <Switch
      value={active}
      onValueChange={(nextActive) => {
        if (disabled) return;
        onToggleActive(category, nextActive);
      }}
      disabled={disabled}
      trackColor={{ false: '#C5D4CE', true: ticketColors.gold }}
      thumbColor="#FFFFFF"
      accessibilityRole="switch"
      accessibilityLabel={label}
      accessibilityState={{ checked: active, disabled }}
      testID={`category-master-toggle-${category.id}`}
    />
  );
}

function ShelfRow({
  category,
  parentActive,
  canEdit,
  togglingCategoryIds,
  canMoveUp,
  canMoveDown,
  reordering,
  reorderMode,
  showTags,
  onOpenDetail,
  onToggleActive,
  onMoveCategory,
}: {
  category: CategoryRecord;
  parentActive: boolean;
  canEdit: boolean;
  togglingCategoryIds: ReadonlySet<string>;
  canMoveUp: boolean;
  canMoveDown: boolean;
  reordering: boolean;
  reorderMode?: boolean;
  showTags?: boolean;
  onOpenDetail: (category: CategoryRecord) => void;
  onToggleActive: (category: CategoryRecord, nextActive: boolean) => void;
  onMoveCategory?: (category: CategoryRecord, direction: -1 | 1) => void;
}) {
  const shelfActive = category.isActive && parentActive;
  const switchDisabled = !canEdit || reorderMode || togglingCategoryIds.has(category.id);

  return (
    <View style={[styles.shelf, !shelfActive ? styles.shelfOff : null]}>
      <Pressable
        style={styles.shelfNamePress}
        onPress={() => onOpenDetail(category)}
        disabled={reorderMode}
        accessibilityRole="button"
        accessibilityLabel={`Ver detalle de ${category.name}`}
      >
        <Text style={[styles.shelfName, !shelfActive ? styles.shelfNameOff : null]} numberOfLines={2}>
          {category.name}
        </Text>
        {showTags ? (
          <Text style={[styles.shelfMeta, !shelfActive ? styles.shelfNameOff : null]} numberOfLines={1}>
            Subcategoría · {categoryAppliesLabel(category)}
          </Text>
        ) : null}
      </Pressable>
      {showTags ? (
        <View style={styles.shelfTags}>
          <Text style={styles.appliesTag} numberOfLines={1}>
            {categoryAppliesLabel(category)}
          </Text>
          <Text style={[styles.stateTag, !shelfActive && styles.stateTagInactive]} numberOfLines={1}>
            {shelfActive ? 'Activa' : 'Oculta'}
          </Text>
        </View>
      ) : null}
      {onMoveCategory ? (
        <ReorderArrows
          category={category}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
          disabled={!canEdit || reordering}
          onMove={onMoveCategory}
        />
      ) : null}
      <CategoryActiveSwitch
        category={category}
        active={shelfActive}
        disabled={switchDisabled}
        label={shelfActive ? `Ocultar ${category.name}` : `Mostrar ${category.name}`}
        onToggleActive={onToggleActive}
      />
    </View>
  );
}

export function CategoryMasterTreeView({
  groups,
  canEdit,
  togglingCategoryIds,
  filterState,
  isWide,
  reordering = false,
  reorderMode = false,
  onOpenDetail,
  onToggleActive,
  onMoveCategory,
}: CategoryMasterTreeViewProps) {
  const publicStoreFeaturesEnabled = usePublicStoreFeaturesVisible();
  const [selectedAisleId, setSelectedAisleId] = useState<string | null>(null);

  const groupIds = useMemo(() => groups.map(({ group }) => group.parent.id), [groups]);

  useEffect(() => {
    setSelectedAisleId((prev) => {
      if (prev && groupIds.includes(prev)) return prev;
      // Default: primera categoría con subcategorías visibles, no la primera de
      // la lista — así el panel derecho no arranca vacío si la primera no tiene hijas.
      return (
        groups.find((item) => item.visibleChildren.length > 0)?.group.parent.id ??
        groupIds[0] ??
        null
      );
    });
  }, [groupIds, groups]);

  if (groups.length === 0) return null;

  const selected =
    groups.find(({ group }) => group.parent.id === selectedAisleId) ?? groups[0] ?? null;

  const aislesHead = (
    <View style={styles.aislesHead}>
      <Text style={styles.aislesHeadTitle}>Categorías principales</Text>
      <Text style={styles.aislesHeadCount}>{groups.length} visibles</Text>
    </View>
  );

  const aisleRows = groups.map(({ group }, index) => {
    const selectedRow = group.parent.id === selected?.group.parent.id;
    const parentName =
      group.parent.name +
      (publicStoreFeaturesEnabled && group.parent.isRestricted ? ' · 18+' : '');
    return (
      <View
        key={group.parent.id}
        style={[styles.aisle, selectedRow ? styles.aisleOn : null]}
        testID={`category-master-aisle-${group.parent.id}`}
      >
        <Pressable
          style={styles.aislePress}
          onPress={() => setSelectedAisleId(group.parent.id)}
          accessibilityRole="button"
          accessibilityLabel={`Ver subcategorías de ${group.parent.name}`}
        >
          <Text style={styles.aisleName} numberOfLines={2}>
            {parentName}
          </Text>
          <View style={styles.aisleMetaRow}>
            <Text style={styles.aisleMeta}>
              {categoryChildrenMeta(group.children.length, group.parent)}
            </Text>
          </View>
        </Pressable>
        <View style={styles.aisleCountPill}>
          <Text style={styles.aisleCountText}>{group.children.length}</Text>
        </View>
        {onMoveCategory ? (
          <ReorderArrows
            category={group.parent}
            canMoveUp={index > 0}
            canMoveDown={index < groups.length - 1}
            disabled={!canEdit || reordering}
            onMove={onMoveCategory}
          />
        ) : null}
        <CategoryActiveSwitch
          category={group.parent}
          active={group.parent.isActive}
          disabled={!canEdit || reorderMode || togglingCategoryIds.has(group.parent.id)}
          label={
            group.parent.isActive
              ? `Ocultar categoría ${group.parent.name}`
              : `Mostrar categoría ${group.parent.name}`
          }
          onToggleActive={onToggleActive}
        />
      </View>
    );
  });

  const shelvesPane = (
    <View style={styles.shelvesPane} testID="category-master-shelves">
        <View style={styles.shelvesHeader}>
          <View style={styles.shelvesHeaderText}>
            <Text style={styles.shelvesTitle} numberOfLines={1}>
              {selected ? selected.group.parent.name : 'Subcategorías'}
            </Text>
            <Text style={styles.shelvesNote} numberOfLines={1}>
              {selected
                ? `${selected.group.children.length} subcategorías · ${
                    selected.group.parent.isActive ? 'Activa' : 'Oculta'
                  }`
                : 'Elegí una categoría principal'}
            </Text>
          </View>
          {selected ? (
            <Pressable
              style={styles.shelvesDetailBtn}
              onPress={() => onOpenDetail(selected.group.parent)}
              disabled={reorderMode}
              accessibilityRole="button"
              accessibilityLabel={`Ver detalle de ${selected.group.parent.name}`}
            >
              <Text style={styles.shelvesDetailText}>Ver detalle</Text>
            </Pressable>
          ) : null}
        </View>
        {!selected ? (
          <Text style={styles.emptyPane}>No hay categorías con este filtro.</Text>
        ) : selected.visibleChildren.length > 0 ? (
          selected.visibleChildren.map((child, childIndex) => (
            <ShelfRow
              key={child.id}
              category={child}
              parentActive={selected.group.parent.isActive}
              canEdit={canEdit}
              togglingCategoryIds={togglingCategoryIds}
              canMoveUp={childIndex > 0}
              canMoveDown={childIndex < selected.visibleChildren.length - 1}
              reordering={reordering}
              reorderMode={reorderMode}
              showTags
              onOpenDetail={onOpenDetail}
              onToggleActive={onToggleActive}
              onMoveCategory={onMoveCategory}
            />
          ))
        ) : !selected.group.parent.isActive && filterState === 'active' ? (
          <Text style={styles.emptyPane}>
            Esta categoría está oculta. Activala a la izquierda para ver las subcategorías.
          </Text>
        ) : (
          <Text style={styles.emptyPane}>No hay subcategorías para mostrar.</Text>
        )}
        {filterState === 'inactive' ? (
          <Text style={styles.deskHint}>
            Al activar una subcategoría, también se activa su categoría principal.
          </Text>
        ) : (
          <Text style={styles.deskHint}>
            Si ocultás una categoría principal, sus subcategorías dejan de mostrarse. Los productos
            no se borran.
          </Text>
        )}
    </View>
  );

  if (!isWide) {
    return (
      <View style={styles.mobileRoot}>
        <Text style={styles.hintBar}>
          {reorderMode
            ? 'Las subcategorías se ordenan dentro de su categoría. Elegí una categoría principal para ver sus subcategorías.'
            : 'Si ocultás una categoría principal, sus subcategorías dejan de mostrarse. Los productos no se borran.'}
        </Text>
        <View style={[styles.aisles, styles.aislesMobile]}>
          {aislesHead}
          <ScrollView
            style={styles.aislesMobileScroll}
            nestedScrollEnabled
            showsVerticalScrollIndicator
          >
            {aisleRows}
          </ScrollView>
        </View>
        {shelvesPane}
      </View>
    );
  }

  return (
    <View style={styles.splitOuter}>
      <View style={styles.aisles}>
        {aislesHead}
        {aisleRows}
      </View>
      {shelvesPane}
    </View>
  );
}

const styles = StyleSheet.create({
  mobileRoot: {
    gap: spacing.sm,
  },
  hintBar: {
    ...typography.caption,
    color: ticketColors.ink2,
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    lineHeight: 18,
  },
  shelf: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: 13,
    paddingVertical: 8,
    minHeight: 52,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  shelfOff: {
    opacity: 0.92,
  },
  shelfNamePress: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  shelfName: {
    ...typography.body,
    color: ticketColors.ink2,
    fontWeight: '700',
    fontSize: 12,
  },
  shelfMeta: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontSize: 10,
  },
  shelfNameOff: {
    color: ticketColors.ink3,
  },
  shelfTags: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  appliesTag: {
    ...typography.caption,
    minHeight: 23,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    color: ticketColors.ink2,
    fontSize: 10,
    fontWeight: '700',
  },
  stateTag: {
    ...typography.caption,
    minWidth: 52,
    minHeight: 23,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(21, 128, 61, 0.2)',
    backgroundColor: ticketColors.brandSoft,
    color: ticketColors.ok,
    fontSize: 10,
    fontWeight: '700',
    textAlign: 'center',
  },
  stateTagInactive: {
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
    color: ticketColors.warn,
  },
  splitOuter: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    minHeight: 420,
  },
  aisles: {
    ...ticketSurfaces.paperSheet,
    width: 245,
    maxWidth: '36%',
    backgroundColor: ticketColors.paper,
  },
  aislesMobile: {
    width: '100%',
    maxWidth: '100%',
  },
  aislesMobileScroll: {
    maxHeight: 228,
  },
  aislesHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingHorizontal: 13,
    paddingVertical: 11,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
  },
  aislesHeadTitle: {
    ...typography.caption,
    color: ticketColors.ink,
    fontSize: 12,
    fontWeight: '800',
  },
  aislesHeadCount: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontSize: 11,
  },
  aisle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: spacing.sm + 2,
    minHeight: 58,
    borderLeftWidth: 3,
    borderLeftColor: 'transparent',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  aisleOn: {
    backgroundColor: ticketColors.tile,
    borderLeftColor: ticketColors.brandPrimary,
  },
  aisleCountPill: {
    minWidth: 25,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
  },
  aisleCountText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontSize: 10,
    fontWeight: '800',
  },
  aislePress: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  aisleName: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    fontSize: 14,
  },
  aisleMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  aisleMeta: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontSize: 11,
    fontWeight: '600',
  },
  shelvesPane: {
    ...ticketSurfaces.paperSheet,
    flex: 1,
    minWidth: 0,
    paddingVertical: 0,
  },
  shelvesHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingHorizontal: 13,
    paddingTop: 13,
    paddingBottom: 10,
  },
  shelvesHeaderText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  shelvesTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontSize: 13,
    fontWeight: '800',
  },
  shelvesNote: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontSize: 11,
  },
  shelvesDetailBtn: {
    minHeight: 34,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    justifyContent: 'center',
  },
  shelvesDetailText: {
    ...typography.caption,
    color: ticketColors.ink,
    fontSize: 11,
    fontWeight: '800',
  },
  emptyPane: {
    ...typography.caption,
    color: ticketColors.ink3,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
    lineHeight: 20,
  },
  moveCol: {
    flexDirection: 'column',
    gap: 2,
  },
  moveBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ticketColors.paper2,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  moveBtnOff: {
    opacity: 0.5,
  },
  deskHint: {
    ...typography.caption,
    color: ticketColors.ink3,
    marginHorizontal: 13,
    paddingTop: 10,
    paddingBottom: 3,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
    fontSize: 11,
    lineHeight: 16,
  },
});
